import {
  type MarketingBundle,
  getMarketingBundle,
} from "@/lib/marketing-attribution";

import { PaymentMethodConfigResponse } from "@/types/patient-portal";
import { PaymentMethodSummary } from "@/types/payment";
import {
  CompletePurchaseWithPatientCreditRequest,
  CompletePurchaseWithPatientCreditResponse,
  CreatePurchasePayinConfigRequest,
  CreatePurchasePayinConfigResponse,
  EnsureMembershipFormEnrollmentRequest,
  EnsureMembershipFormEnrollmentResponse,
  PublicShopPromotion,
  ShopCatalogApiResponse,
  ShopCatalogResponse,
  ShopChargeSavedMethodResponse,
  ShopPurchasePricingResponse,
} from "@/types/shop";

import { requestData } from "@/api/api-config";

/**
 * Attach the shopper's marketing journey to a checkout request.
 *
 * Done here rather than at each call site so every checkout path — hosted
 * card, saved card, patient credit — carries it, and adding a fourth cannot
 * silently drop attribution. Reading it is wrapped, so a shopper never loses
 * a purchase to a storage failure.
 */
function withMarketing<T extends object>(
  body: T,
): T & { marketing?: MarketingBundle } {
  return { ...body, marketing: getMarketingBundle() };
}

export function resolveShopCatalog(
  response: ShopCatalogApiResponse,
): ShopCatalogResponse {
  const offersById = new Map(
    response.offers.map((offer) => [offer.offerId, offer]),
  );
  const resolveOffers = (offerIds: string[]): PublicShopPromotion[] =>
    offerIds.flatMap((offerId) => {
      const offer = offersById.get(offerId);
      return offer ? [offer] : [];
    });

  return {
    categories: response.categories,
    promotions: response.offers,
    shipping: response.shipping ?? null,
    items: response.items.map(
      ({ applicableOfferIds, availableOfferIds, bestOfferId, ...item }) => ({
        ...item,
        applicablePromotions: resolveOffers(applicableOfferIds),
        availablePromotions: resolveOffers(availableOfferIds),
        bestPromotion: bestOfferId
          ? (offersById.get(bestOfferId) ?? null)
          : null,
      }),
    ),
  };
}

async function fetchResolvedShopCatalog(
  path: "shop/catalog" | "patient/shop/catalog",
  options?: { cache: RequestCache },
): Promise<ShopCatalogResponse | null> {
  const response = await requestData<ShopCatalogApiResponse>(
    path,
    "get",
    options,
    false,
  );
  return response ? resolveShopCatalog(response) : null;
}

export async function fetchShopCatalog(): Promise<ShopCatalogResponse | null> {
  return await fetchResolvedShopCatalog("shop/catalog");
}

/** Editor saves need a fresh writer-backed projection, not the 60s HTTP cache. */
export async function fetchFreshShopCatalog(): Promise<ShopCatalogResponse | null> {
  return await fetchResolvedShopCatalog("shop/catalog", { cache: "no-store" });
}

export async function fetchPatientShopCatalog(): Promise<ShopCatalogResponse | null> {
  return await fetchResolvedShopCatalog("patient/shop/catalog");
}

export async function fetchPurchasePricing(
  body: CreatePurchasePayinConfigRequest,
): Promise<ShopPurchasePricingResponse | null> {
  return await requestData<ShopPurchasePricingResponse>(
    "patient/shop/pricing",
    "post",
    { json: body },
    false,
  );
}

export async function ensureMembershipFormEnrollment(
  body: EnsureMembershipFormEnrollmentRequest,
): Promise<EnsureMembershipFormEnrollmentResponse | null> {
  return await requestData<EnsureMembershipFormEnrollmentResponse>(
    "patient/shop/membership-form-enrollment",
    "post",
    { json: body },
    false,
  );
}

export async function createMembershipPaymentMethodConfig(
  body: CreatePurchasePayinConfigRequest,
): Promise<PaymentMethodConfigResponse | null> {
  return await requestData<PaymentMethodConfigResponse>(
    "patient/shop/membership-payment-method-config",
    "post",
    { json: body },
    false,
  );
}

export async function saveMembershipPaymentMethod(
  body: CreatePurchasePayinConfigRequest & {
    rainforestPaymentMethodId: string;
    paymentMethodConfigId: string;
  },
): Promise<PaymentMethodSummary | null> {
  return await requestData<PaymentMethodSummary>(
    "patient/shop/membership-payment-method",
    "post",
    { json: body },
    false,
  );
}

export async function createPurchasePayinConfig(
  body: CreatePurchasePayinConfigRequest,
): Promise<CreatePurchasePayinConfigResponse | null> {
  return await requestData<CreatePurchasePayinConfigResponse>(
    "patient/shop/payin-config",
    "post",
    { json: withMarketing(body) },
    false,
  );
}

export async function completePurchaseWithPatientCredit(
  body: CompletePurchaseWithPatientCreditRequest,
): Promise<CompletePurchaseWithPatientCreditResponse | null> {
  return await requestData<CompletePurchaseWithPatientCreditResponse>(
    "patient/shop/patient-credit",
    "post",
    { json: withMarketing(body), timeout: 60_000 },
    false,
  );
}

export async function chargePurchaseWithSavedMethod(
  body: CreatePurchasePayinConfigRequest & { paymentMethodId: string },
): Promise<ShopChargeSavedMethodResponse | null> {
  return await requestData<ShopChargeSavedMethodResponse>(
    "patient/shop/charge-saved-method",
    "post",
    { json: withMarketing(body) },
  );
}
